import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import {
  ArrowLeft, Building2, MapPin, Calendar, Users, User, Plus, Loader2,
  ChevronsUpDown, ChevronDown, Check, FileText, Package, Phone, Mail,
  X, Upload, Eye, EyeOff, File as FileIcon, Trash2, Receipt,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parse } from 'date-fns';
import { TimeField } from '../components/ui/time-field';
import { cn } from '../lib/utils';
import { TowerCraneAllocations } from '../components/tower-crane/TowerCraneAllocations';
import { TowerCraneDocketsInvoicing } from '../components/tower-crane/TowerCraneDocketsInvoicing';

const BILLING_PERIODS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'fortnightly', label: 'Fortnightly' },
  { value: 'monthly', label: 'Monthly' },
];

const DURATION_OPTIONS = [0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10, 10.5, 11, 11.5, 12];

export default function TowerCraneJobCreate() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const { jobId } = useParams();
  const isEdit = !!jobId;
  const fileInputRef = useRef(null);

  // Lookups
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [workerTypes, setWorkerTypes] = useState([]);
  const [loadingLookups, setLoadingLookups] = useState(true);
  const [loadingJob, setLoadingJob] = useState(!!jobId);
  const [jobMeta, setJobMeta] = useState({}); // non-editable fields (job_number, status, etc.)

  // Contacts
  const [customerContacts, setCustomerContacts] = useState([]);
  const [siteContacts, setSiteContacts] = useState([]);

  // Existing files (edit mode)
  const [existingFiles, setExistingFiles] = useState([]);

  // Form state
  const [form, setForm] = useState({
    customer_id: '',
    contact_id: '',
    site_id: '',
    site_contact_ids: [],
    equipment_type: '',
    description: '',
    notes: '',
    po_number: '',
    mobilisation_date: '',
    start_date: format(new Date(), 'yyyy-MM-dd'),
    start_time: '07:00',
    end_time: '',
    est_duration: '',
    expected_off_hire_date: '',
    tower_crane_billing_period: 'weekly',
    include_weekends_in_rate: null,
    worker_requirements: [],
    tags: [],
  });

  // Pending files for upload
  const [pendingFiles, setPendingFiles] = useState([]);
  const [pendingFilesVisible, setPendingFilesVisible] = useState(false);

  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);

  // Combobox open states
  const [customerOpen, setCustomerOpen] = useState(false);
  const [siteOpen, setSiteOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [siteContactOpen, setSiteContactOpen] = useState(false);
  const [equipTypeOpen, setEquipTypeOpen] = useState(false);

  // Date picker states
  const [mobDateOpen, setMobDateOpen] = useState(false);
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [offHireDateOpen, setOffHireDateOpen] = useState(false);

  // Dialog states
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', phone: '', email: '', position: '' });
  const [siteContactDialogOpen, setSiteContactDialogOpen] = useState(false);
  const [newSiteContact, setNewSiteContact] = useState({ name: '', job_title: '', phone: '', email: '' });
  const [savingContact, setSavingContact] = useState(false);

  // Tab state (Details vs Dockets/Invoicing)
  const [activeTab, setActiveTab] = useState('details');

  // ── Fetch lookups ──
  useEffect(() => {
    const load = async () => {
      try {
        const [custRes, siteRes, wtRes, initRes] = await Promise.all([
          api.get('/customers'),
          api.get('/sites'),
          api.get('/worker-types'),
          api.get('/v2/schedule/init'),
        ]);
        setCustomers(custRes.data || []);
        setSites(siteRes.data || []);
        setWorkerTypes(wtRes.data || []);
        const eqTypes = initRes.data?.filters?.equipment_types || [];
        setEquipmentTypes(eqTypes);
        // Auto-select Tower Crane only for create
        if (!jobId) {
          const tcType = eqTypes.find(et => et.name?.toLowerCase().includes('tower crane'));
          if (tcType) setForm(f => ({ ...f, equipment_type: tcType.name }));
        }
      } catch { toast.error('Failed to load form data'); }
      finally { setLoadingLookups(false); }
    };
    load();
  }, [api, jobId]);

  // ── Load existing job (edit mode) ──
  useEffect(() => {
    if (!jobId) return;
    const loadJob = async () => {
      try {
        const res = await api.get(`/jobs/${jobId}`);
        const job = res.data;
        setJobMeta({ job_number: job.job_number, status: job.status, id: job.id });
        setForm({
          customer_id: job.customer_id || '',
          contact_id: job.contact_id || '',
          site_id: job.site_id || '',
          site_contact_ids: job.site_contact_ids || [],
          equipment_type: job.equipment_type || '',
          description: job.description || '',
          notes: job.notes || '',
          po_number: job.po_number || '',
          mobilisation_date: job.mobilisation_date || '',
          start_date: job.start_date || '',
          start_time: job.start_time || '07:00',
          end_time: job.end_time || '',
          est_duration: job.est_duration != null ? job.est_duration : '',
          expected_off_hire_date: job.expected_off_hire_date || '',
          tower_crane_billing_period: job.tower_crane_billing_period || 'weekly',
          include_weekends_in_rate: job.include_weekends_in_rate ?? null,
          worker_requirements: job.worker_requirements || [],
          tags: job.tags || [],
        });
        // Load existing files
        try {
          const filesRes = await api.get(`/jobs/${jobId}/files`);
          setExistingFiles(filesRes.data || []);
        } catch { /* ignore */ }
      } catch { toast.error('Failed to load job'); navigate('/schedule?view=towercrane'); }
      finally { setLoadingJob(false); }
    };
    loadJob();
  }, [api, jobId]);

  // Filtered sites
  const filteredSites = useMemo(() => {
    if (!form.customer_id) return sites;
    return sites.filter(s => s.customer_id === form.customer_id);
  }, [sites, form.customer_id]);

  const selectedCustomer = useMemo(() => customers.find(c => c.id === form.customer_id), [customers, form.customer_id]);
  const selectedSite = useMemo(() => sites.find(s => s.id === form.site_id), [sites, form.site_id]);

  // Fetch contacts
  const fetchCustomerContacts = useCallback(async (cid) => {
    if (!cid) { setCustomerContacts([]); return; }
    try { const r = await api.get(`/customers/${cid}/contacts`); setCustomerContacts(r.data || []); } catch { setCustomerContacts([]); }
  }, [api]);

  const fetchSiteContacts = useCallback(async (sid) => {
    if (!sid) { setSiteContacts([]); return; }
    try { const r = await api.get(`/sites/${sid}/contacts`); setSiteContacts(r.data || []); } catch { setSiteContacts([]); }
  }, [api]);

  useEffect(() => {
    if (form.customer_id) {
      fetchCustomerContacts(form.customer_id);
      if (form.site_id) {
        const site = sites.find(s => s.id === form.site_id);
        if (site && site.customer_id !== form.customer_id) {
          setForm(f => ({ ...f, site_id: '', site_contact_ids: [], contact_id: '' }));
          setSiteContacts([]);
        }
      }
    } else { setCustomerContacts([]); }
  }, [form.customer_id]);

  useEffect(() => { if (form.site_id) fetchSiteContacts(form.site_id); else setSiteContacts([]); }, [form.site_id]);

  useEffect(() => {
    if (selectedCustomer?.tower_crane_billing_period && !isEdit) {
      setForm(f => ({ ...f, tower_crane_billing_period: selectedCustomer.tower_crane_billing_period }));
    }
  }, [selectedCustomer]);

  const setField = useCallback((key, value) => setForm(f => ({ ...f, [key]: value })), []);

  // ── Duration ↔ End Time sync ──
  const handleDurationChange = (dur) => {
    setForm(prev => {
      const updates = { ...prev, est_duration: dur };
      if (dur && prev.start_time) {
        const [h, m] = prev.start_time.split(':').map(Number);
        const dH = Math.floor(dur), dM = (dur % 1) * 60;
        let eH = h + dH, eM = m + dM;
        if (eM >= 60) { eH += 1; eM -= 60; }
        if (eH >= 24) eH -= 24;
        updates.end_time = `${eH.toString().padStart(2, '0')}:${Math.round(eM).toString().padStart(2, '0')}`;
      }
      return updates;
    });
  };

  const handleEndTimeChange = (v) => {
    setForm(prev => {
      if (!v) return { ...prev, end_time: '', est_duration: '' };
      const updates = { ...prev, end_time: v };
      if (prev.start_time) {
        const [sH, sM] = prev.start_time.split(':').map(Number);
        const [eH, eM] = v.split(':').map(Number);
        let diff = (eH * 60 + eM) - (sH * 60 + sM);
        if (diff < 0) diff += 24 * 60;
        const durHours = Math.round(diff / 30) * 0.5;
        updates.est_duration = (durHours > 0 && durHours <= 12) ? durHours : '';
      }
      return updates;
    });
  };

  // ── Customer contact handlers ──
  const handleCreateContact = async (e) => {
    e.preventDefault();
    setSavingContact(true);
    try {
      const res = await api.post(`/customers/${form.customer_id}/contacts`, { ...contactForm, customer_id: form.customer_id });
      toast.success('Contact created');
      setContactDialogOpen(false);
      await fetchCustomerContacts(form.customer_id);
      setField('contact_id', res.data.id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create contact'); }
    finally { setSavingContact(false); }
  };

  // ── Site contact handlers ──
  const handleCreateSiteContact = async (e) => {
    e.preventDefault();
    if (!form.site_id || !newSiteContact.name.trim()) { toast.error('Contact name is required'); return; }
    setSavingContact(true);
    try {
      const res = await api.post(`/sites/${form.site_id}/contacts`, {
        site_id: form.site_id, name: newSiteContact.name.trim(),
        job_title: newSiteContact.job_title.trim() || null, phone: newSiteContact.phone.trim() || null, email: newSiteContact.email.trim() || null,
      });
      await fetchSiteContacts(form.site_id);
      if (res.data?.id) setForm(f => ({ ...f, site_contact_ids: [...(f.site_contact_ids || []), res.data.id] }));
      setNewSiteContact({ name: '', job_title: '', phone: '', email: '' });
      setSiteContactDialogOpen(false);
      toast.success('Site contact added');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add site contact'); }
    finally { setSavingContact(false); }
  };

  const toggleSiteContact = (contactId) => {
    setForm(f => ({ ...f, site_contact_ids: (f.site_contact_ids || []).includes(contactId) ? f.site_contact_ids.filter(id => id !== contactId) : [...(f.site_contact_ids || []), contactId] }));
  };

  // ── Worker requirements (checkbox + stepper) ──
  const toggleWorkerType = (typeId, checked) => {
    setForm(f => ({ ...f, worker_requirements: checked ? [...f.worker_requirements, { worker_type_id: typeId, quantity: 1 }] : f.worker_requirements.filter(wr => wr.worker_type_id !== typeId) }));
  };
  const updateWorkerQty = (typeId, delta) => {
    setForm(f => ({ ...f, worker_requirements: f.worker_requirements.map(wr => wr.worker_type_id === typeId ? { ...wr, quantity: Math.max(1, wr.quantity + delta) } : wr) }));
  };

  // Tags
  const addTag = () => { const t = tagInput.trim(); if (t && !form.tags.includes(t)) { setForm(f => ({ ...f, tags: [...f.tags, t] })); setTagInput(''); } };
  const removeTag = (tag) => setForm(f => ({ ...f, tags: f.tags.filter(t => t !== tag) }));

  // Validation
  const validate = () => {
    if (!form.customer_id) { toast.error('Customer is required'); return false; }
    if (!form.site_id) { toast.error('Site is required'); return false; }
    if (!form.equipment_type) { toast.error('Crane type is required'); return false; }
    if (!form.start_date) { toast.error('Start date is required'); return false; }
    if (!form.start_time) { toast.error('Start time is required'); return false; }
    if (!form.description?.trim()) { toast.error('Job description is required'); return false; }
    return true;
  };

  // Submit (create or update)
  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        customer_id: form.customer_id,
        contact_id: form.contact_id || null,
        site_id: form.site_id,
        site_contact_ids: form.site_contact_ids || [],
        equipment_type: form.equipment_type,
        description: form.description,
        notes: form.notes || '',
        po_number: form.po_number || '',
        start_date: form.start_date,
        start_time: form.start_time,
        end_time: form.end_time || '',
        est_duration: form.est_duration || null,
        hire_type: 'dry',
        hire_period_unit: 'week',
        is_tower_crane: true,
        tower_crane_billing_period: form.tower_crane_billing_period || 'weekly',
        include_weekends_in_rate: form.include_weekends_in_rate,
        mobilisation_date: form.mobilisation_date || null,
        expected_off_hire_date: form.expected_off_hire_date || null,
        worker_requirements: form.worker_requirements.filter(wr => wr.worker_type_id),
        tags: form.tags,
      };

      let targetJobId = jobId;
      if (isEdit) {
        await api.put(`/jobs/${jobId}`, payload);
        toast.success('Tower Crane job updated');
      } else {
        payload.status = 'not_allocated';
        const res = await api.post('/jobs', payload);
        targetJobId = res.data?.id;
        toast.success(`Tower Crane job ${res.data?.job_number || ''} created`);
      }

      // Upload pending files
      if (targetJobId && pendingFiles.length > 0) {
        let n = 0;
        for (const pf of pendingFiles) {
          const fd = new FormData();
          fd.append('file', pf.file);
          fd.append('visible_to_operators', pf.visibleToOperators);
          try { await api.post(`/jobs/${targetJobId}/files`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); n++; } catch { toast.error(`Failed to upload ${pf.file.name}`); }
        }
        if (n > 0) toast.success(`${n} file${n > 1 ? 's' : ''} attached`);
      }

      navigate('/schedule?view=towercrane');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save job'); }
    finally { setSaving(false); }
  };

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '';
    try { return format(parse(dateStr, 'yyyy-MM-dd', new Date()), 'EEE, dd MMM yyyy'); } catch { return dateStr; }
  };

  const goBack = () => navigate('/schedule?view=towercrane');

  if (loadingLookups || loadingJob) {
    return (<div className="flex items-center justify-center min-h-screen bg-background"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>);
  }

  return (
    <div className="min-h-screen bg-background" data-testid="tc-job-create-page">
      {/* Top bar */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={goBack} data-testid="tc-create-back-btn"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
            <div className="h-5 w-px bg-border" />
            <h1 className="text-lg font-semibold tracking-tight">{isEdit ? 'Manage Tower Crane Job' : 'Create Tower Crane Job'}</h1>
          </div>
          <div className="flex items-center gap-2">
            {activeTab === 'details' && (
              <>
                <Button variant="outline" size="sm" onClick={goBack} data-testid="tc-create-cancel-btn">Cancel</Button>
                <Button size="sm" onClick={handleSubmit} disabled={saving} className="bg-[#ffd108] text-black hover:bg-[#e5bc07] min-w-[120px]" data-testid="tc-create-submit-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {isEdit ? 'Save Changes' : 'Create Job'}
                </Button>
              </>
            )}
          </div>
        </div>
        {/* Tabs — only in edit/manage mode */}
        {isEdit && (
          <div className="max-w-5xl mx-auto px-6">
            <div className="flex gap-0 border-b-0" data-testid="tc-page-tabs">
              <button
                className={cn(
                  "px-4 py-2 text-sm font-medium border-b-2 transition-colors",
                  activeTab === 'details'
                    ? "border-[#ffd108] text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/30"
                )}
                onClick={() => setActiveTab('details')}
                data-testid="tc-tab-details"
              >
                Details
              </button>
              <button
                className={cn(
                  "px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5",
                  activeTab === 'dockets'
                    ? "border-[#ffd108] text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/30"
                )}
                onClick={() => setActiveTab('dockets')}
                data-testid="tc-tab-dockets"
              >
                <Receipt className="h-3.5 w-3.5" />
                Dockets / Invoicing
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Dockets / Invoicing tab content */}
      {isEdit && activeTab === 'dockets' && (
        <div className="max-w-5xl mx-auto px-6 py-6">
          <TowerCraneDocketsInvoicing jobId={jobId} />
        </div>
      )}

      {/* Form body (Details tab) */}
      {activeTab === 'details' && (
      <div className="max-w-5xl mx-auto px-6 py-6 space-y-6">

        {/* ───── Row 1: Customer & Site ───── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Customer */}
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-sm font-medium flex items-center gap-2"><Building2 className="h-4 w-4 text-muted-foreground" /> Customer</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs text-muted-foreground">Customer *</Label>
                <Popover open={customerOpen} onOpenChange={setCustomerOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="tc-create-customer-select">
                      {selectedCustomer?.company_name || 'Select customer...'}<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[400px] p-0" align="start">
                    <Command><CommandInput placeholder="Search customers..." /><CommandList><CommandEmpty>No customer found.</CommandEmpty><CommandGroup>
                      {customers.map(c => (<CommandItem key={c.id} value={c.company_name} onSelect={() => { setForm(f => ({ ...f, customer_id: c.id, contact_id: '', site_id: '', site_contact_ids: [] })); setCustomerOpen(false); }}><Check className={cn('mr-2 h-4 w-4', form.customer_id === c.id ? 'opacity-100' : 'opacity-0')} />{c.company_name}</CommandItem>))}
                    </CommandGroup></CommandList></Command>
                  </PopoverContent>
                </Popover>
              </div>
              {form.customer_id && (
                <div>
                  <Label className="text-xs text-muted-foreground">Contact</Label>
                  <Popover open={contactOpen} onOpenChange={setContactOpen} modal>
                    <PopoverTrigger asChild>
                      <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="tc-create-contact-select">
                        {form.contact_id ? <span className="flex items-center gap-2 truncate"><User className="h-3 w-3 text-muted-foreground" />{customerContacts.find(c => c.id === form.contact_id)?.name || 'Select'}</span> : <span className="text-muted-foreground">Select contact (optional)</span>}
                        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                      <Command><CommandInput placeholder="Search contacts..." /><CommandList className="max-h-[300px]"><CommandEmpty>No contact found.</CommandEmpty>
                        <CommandGroup><CommandItem value="__add_new_customer_contact__" onSelect={() => { setContactForm({ name: '', phone: '', email: '', position: '' }); setContactDialogOpen(true); setContactOpen(false); }} className="text-[#ffd108] font-medium"><Plus className="mr-2 h-4 w-4" /> Add New Contact</CommandItem></CommandGroup>
                        {customerContacts.length > 0 && (<CommandGroup heading="Contacts">{customerContacts.map(c => (<CommandItem key={c.id} value={c.name || `c-${c.id}`} onSelect={() => { setField('contact_id', c.id); setContactOpen(false); }}><Check className={cn('mr-2 h-4 w-4', form.contact_id === c.id ? 'opacity-100' : 'opacity-0')} />{c.name}{c.position && <span className="text-xs text-muted-foreground ml-1">({c.position})</span>}</CommandItem>))}</CommandGroup>)}
                      </CommandList></Command>
                    </PopoverContent>
                  </Popover>
                </div>
              )}
              {selectedCustomer && (<div className="text-xs text-muted-foreground space-y-1">
                {selectedCustomer.requires_po && <Badge variant="outline" className="text-[10px] border-amber-500 text-amber-400">PO Required</Badge>}
                {selectedCustomer.tower_crane_billing_period && <p>Default billing: {BILLING_PERIODS.find(b => b.value === selectedCustomer.tower_crane_billing_period)?.label || selectedCustomer.tower_crane_billing_period}</p>}
              </div>)}
              {selectedCustomer?.requires_po && (<div><Label className="text-xs text-muted-foreground">PO Number *</Label><Input value={form.po_number} onChange={e => setField('po_number', e.target.value)} placeholder="Enter PO number" data-testid="tc-create-po-number" /></div>)}
            </CardContent>
          </Card>

          {/* Site */}
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-sm font-medium flex items-center gap-2"><MapPin className="h-4 w-4 text-muted-foreground" /> Site</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs text-muted-foreground">Site *</Label>
                <Popover open={siteOpen} onOpenChange={setSiteOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="tc-create-site-select">
                      {selectedSite?.name || 'Select site...'}<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[400px] p-0" align="start">
                    <Command><CommandInput placeholder="Search sites..." /><CommandList><CommandEmpty>No site found.</CommandEmpty><CommandGroup>
                      {filteredSites.map(s => (<CommandItem key={s.id} value={`${s.name} ${s.address || ''}`} onSelect={() => { setForm(f => ({ ...f, site_id: s.id, site_contact_ids: [] })); if (!form.customer_id && s.customer_id) setField('customer_id', s.customer_id); setSiteOpen(false); }}><Check className={cn('mr-2 h-4 w-4', form.site_id === s.id ? 'opacity-100' : 'opacity-0')} /><div><div>{s.name}</div>{s.address && <div className="text-xs text-muted-foreground">{s.address}</div>}</div></CommandItem>))}
                    </CommandGroup></CommandList></Command>
                  </PopoverContent>
                </Popover>
              </div>
              {form.site_id && (
                <div data-testid="tc-site-contact-section">
                  <Label className="text-xs text-muted-foreground flex items-center gap-1"><User className="h-3 w-3" /> Site Contacts</Label>
                  <Popover open={siteContactOpen} onOpenChange={setSiteContactOpen} modal>
                    <PopoverTrigger asChild>
                      <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="tc-create-site-contact-select">
                        {form.site_contact_ids?.length > 0 ? <span className="truncate">{form.site_contact_ids.length} contact{form.site_contact_ids.length > 1 ? 's' : ''} selected</span> : <span className="text-muted-foreground">Select site contacts...</span>}
                        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                      <Command><CommandInput placeholder="Search site contacts..." /><CommandList className="max-h-[300px]"><CommandEmpty>No site contact found.</CommandEmpty>
                        <CommandGroup><CommandItem value="__add_new_site_contact__" onSelect={() => { setNewSiteContact({ name: '', job_title: '', phone: '', email: '' }); setSiteContactDialogOpen(true); setSiteContactOpen(false); }} className="text-[#ffd108] font-medium"><Plus className="mr-2 h-4 w-4" /> Add New Site Contact</CommandItem></CommandGroup>
                        {siteContacts.length > 0 && (<CommandGroup heading="Site Contacts">{siteContacts.map(c => { const sel = form.site_contact_ids?.includes(c.id); return (<CommandItem key={c.id} value={c.name || `sc-${c.id}`} onSelect={() => toggleSiteContact(c.id)}><Check className={cn('mr-2 h-4 w-4', sel ? 'opacity-100' : 'opacity-0')} />{c.name}{c.job_title && <span className="text-xs text-muted-foreground ml-1">({c.job_title})</span>}{c.is_primary && <Badge variant="outline" className="text-xs ml-1">Primary</Badge>}</CommandItem>); })}</CommandGroup>)}
                      </CommandList></Command>
                    </PopoverContent>
                  </Popover>
                  {form.site_contact_ids?.length > 0 && (<div className="space-y-1.5 mt-2">
                    {form.site_contact_ids.map(id => { const c = siteContacts.find(sc => sc.id === id); if (!c) return null; return (
                      <div key={id} className="flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 min-w-0">
                          <span className="font-medium truncate">{c.name}</span>
                          {c.is_primary && <Badge variant="outline" className="text-xs">Primary</Badge>}
                          {c.phone && <span className="flex items-center gap-1 text-muted-foreground text-xs"><Phone className="h-3 w-3" />{c.phone}</span>}
                          {c.email && <span className="flex items-center gap-1 text-muted-foreground text-xs"><Mail className="h-3 w-3" />{c.email}</span>}
                        </div>
                        <Button type="button" variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={() => toggleSiteContact(id)}><X className="h-3.5 w-3.5" /></Button>
                      </div>); })}
                  </div>)}
                </div>
              )}
              {selectedSite?.address && <p className="text-xs text-muted-foreground">{selectedSite.address}</p>}
            </CardContent>
          </Card>
        </div>

        {/* ───── Row 2: Tower Crane & Schedule ───── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-sm font-medium flex items-center gap-2"><Package className="h-4 w-4 text-muted-foreground" /> Equipment & Resources</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs text-muted-foreground">Crane Type *</Label>
                <Popover open={equipTypeOpen} onOpenChange={setEquipTypeOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="tc-create-equipment-type">
                      {form.equipment_type || 'Select crane type...'}<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[400px] p-0" align="start">
                    <Command><CommandInput placeholder="Search crane types..." /><CommandList><CommandEmpty>No type found.</CommandEmpty><CommandGroup>
                      {equipmentTypes.map(et => (<CommandItem key={et.id || et.name} value={et.name} onSelect={() => { setField('equipment_type', et.name); setEquipTypeOpen(false); }}><Check className={cn('mr-2 h-4 w-4', form.equipment_type === et.name ? 'opacity-100' : 'opacity-0')} />{et.name}</CommandItem>))}
                    </CommandGroup></CommandList></Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Billing Period</Label>
                <Select value={form.tower_crane_billing_period} onValueChange={v => setField('tower_crane_billing_period', v)}>
                  <SelectTrigger data-testid="tc-create-billing-period"><SelectValue placeholder="Select billing period" /></SelectTrigger>
                  <SelectContent>{BILLING_PERIODS.map(b => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between py-1">
                <Label className="text-xs text-muted-foreground cursor-pointer" htmlFor="tc-include-weekends">Include weekends in rate</Label>
                <Switch
                  id="tc-include-weekends"
                  checked={form.include_weekends_in_rate === true}
                  onCheckedChange={checked => setField('include_weekends_in_rate', checked)}
                  data-testid="tc-include-weekends-toggle"
                />
              </div>
              {/* Worker Requirements */}
              <div className="pt-1">
                <Label className="text-xs text-muted-foreground flex items-center gap-1.5 mb-2"><Users className="h-3 w-3" /> Worker Requirements</Label>
                <div className="border border-border rounded-md p-3 space-y-3">
                  {workerTypes.length === 0 && <p className="text-xs text-muted-foreground italic">No worker types configured</p>}
                  {workerTypes.map(type => {
                    const existing = form.worker_requirements.find(wr => wr.worker_type_id === type.id);
                    return (
                      <div key={type.id} className="flex items-center gap-3" data-testid={`tc-worker-type-row-${type.id}`}>
                        <Checkbox id={`tc-wt-${type.id}`} checked={!!existing} onCheckedChange={checked => toggleWorkerType(type.id, checked)} data-testid={`tc-wt-check-${type.id}`} />
                        <label htmlFor={`tc-wt-${type.id}`} className="text-sm cursor-pointer flex-1">{type.name}</label>
                        {existing && (
                          <div className="flex items-center gap-1">
                            <Button type="button" variant="outline" size="icon" className="h-6 w-6" onClick={() => updateWorkerQty(type.id, -1)} disabled={existing.quantity <= 1}>-</Button>
                            <span className="text-sm font-medium w-6 text-center">{existing.quantity}</span>
                            <Button type="button" variant="outline" size="icon" className="h-6 w-6" onClick={() => updateWorkerQty(type.id, 1)}>+</Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-sm font-medium flex items-center gap-2"><Calendar className="h-4 w-4 text-muted-foreground" /> Schedule</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {/* Mobilisation Date — first */}
              <div>
                <Label className="text-xs text-muted-foreground">Mobilisation Date</Label>
                <Popover open={mobDateOpen} onOpenChange={setMobDateOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="tc-create-mob-date">
                      <Calendar className="mr-2 h-4 w-4" />{form.mobilisation_date ? formatDateDisplay(form.mobilisation_date) : 'Not set'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent mode="single" selected={form.mobilisation_date ? parse(form.mobilisation_date, 'yyyy-MM-dd', new Date()) : undefined} onSelect={d => { if (d) setField('mobilisation_date', format(d, 'yyyy-MM-dd')); setMobDateOpen(false); }} />
                    {form.mobilisation_date && <div className="p-2 border-t"><Button variant="ghost" size="sm" className="w-full text-xs" onClick={() => { setField('mobilisation_date', ''); setMobDateOpen(false); }}>Clear</Button></div>}
                  </PopoverContent>
                </Popover>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Start Date *</Label>
                  <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                    <PopoverTrigger asChild><Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="tc-create-start-date"><Calendar className="mr-2 h-4 w-4" />{form.start_date ? formatDateDisplay(form.start_date) : 'Pick date'}</Button></PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start"><CalendarComponent mode="single" selected={form.start_date ? parse(form.start_date, 'yyyy-MM-dd', new Date()) : undefined} onSelect={d => { if (d) setField('start_date', format(d, 'yyyy-MM-dd')); setStartDateOpen(false); }} /></PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Expected Off-Hire</Label>
                  <Popover open={offHireDateOpen} onOpenChange={setOffHireDateOpen}>
                    <PopoverTrigger asChild><Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="tc-create-offhire-date"><Calendar className="mr-2 h-4 w-4" />{form.expected_off_hire_date ? formatDateDisplay(form.expected_off_hire_date) : 'Open-ended'}</Button></PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent mode="single" selected={form.expected_off_hire_date ? parse(form.expected_off_hire_date, 'yyyy-MM-dd', new Date()) : undefined} onSelect={d => { if (d) setField('expected_off_hire_date', format(d, 'yyyy-MM-dd')); setOffHireDateOpen(false); }} />
                      {form.expected_off_hire_date && <div className="p-2 border-t"><Button variant="ghost" size="sm" className="w-full text-xs" onClick={() => { setField('expected_off_hire_date', ''); setOffHireDateOpen(false); }}>Clear (open-ended)</Button></div>}
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Start Time *</Label>
                  <TimeField value={form.start_time || '07:00'} onChange={v => setField('start_time', v || '07:00')} testId="tc-create-start-time" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Est. Duration</Label>
                  <Select value={form.est_duration?.toString() || 'none'} onValueChange={v => handleDurationChange(v === 'none' ? '' : parseFloat(v))}>
                    <SelectTrigger className="font-mono" data-testid="tc-create-est-duration"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-</SelectItem>
                      {DURATION_OPTIONS.map(h => <SelectItem key={h} value={h.toString()}>{h} {h === 1 ? 'hour' : 'hours'}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">End Time</Label>
                  <TimeField value={form.end_time || ''} onChange={handleEndTimeChange} allowNone testId="tc-create-end-time" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ───── Row 3: Allocations (edit) or Job Details ───── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Allocations section - only in edit mode */}
          {isEdit ? (
            <TowerCraneAllocations jobId={jobId} jobData={{ ...form, ...jobMeta }} />
          ) : (
            <div /> /* placeholder for grid alignment in create mode */
          )}

          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-sm font-medium flex items-center gap-2"><FileText className="h-4 w-4 text-muted-foreground" /> Job Details</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div><Label className="text-xs text-muted-foreground">Description *</Label><Textarea value={form.description} onChange={e => setField('description', e.target.value)} placeholder="Describe the tower crane job scope..." rows={3} data-testid="tc-create-description" /></div>
              <div><Label className="text-xs text-muted-foreground">Internal Notes</Label><Textarea value={form.notes} onChange={e => setField('notes', e.target.value)} placeholder="Notes visible to office staff only" rows={2} data-testid="tc-create-notes" /></div>
              {!selectedCustomer?.requires_po && (<div><Label className="text-xs text-muted-foreground">PO Number</Label><Input value={form.po_number} onChange={e => setField('po_number', e.target.value)} placeholder="Optional" data-testid="tc-create-po-number" /></div>)}
              <div>
                <Label className="text-xs text-muted-foreground">Tags</Label>
                <div className="flex gap-2"><Input value={tagInput} onChange={e => setTagInput(e.target.value)} placeholder="Add tag" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }} className="flex-1" data-testid="tc-create-tag-input" /><Button variant="outline" size="sm" onClick={addTag} data-testid="tc-create-add-tag">Add</Button></div>
                {form.tags.length > 0 && (<div className="flex flex-wrap gap-1 mt-2">{form.tags.map(tag => <Badge key={tag} variant="secondary" className="text-xs cursor-pointer" onClick={() => removeTag(tag)}>{tag} &times;</Badge>)}</div>)}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ───── Row 4: Documents ───── */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-sm font-medium flex items-center gap-2"><Upload className="h-4 w-4 text-muted-foreground" /> Documents</CardTitle></CardHeader>
          <CardContent>
            {/* Existing files (edit mode) */}
            {existingFiles.length > 0 && (
              <div className="space-y-1 mb-3">
                <Label className="text-xs text-muted-foreground">Attached Files</Label>
                {existingFiles.map(f => (
                  <div key={f.id} className="flex items-center gap-2 px-2 py-1.5 border rounded text-xs bg-muted/30">
                    <FileIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <a href={f.url} target="_blank" rel="noopener noreferrer" className="flex-1 truncate text-primary hover:underline">{f.file_name || f.filename || 'File'}</a>
                    {f.visible_to_operators ? <Eye className="h-3.5 w-3.5 text-green-500" /> : <EyeOff className="h-3.5 w-3.5 text-muted-foreground/40" />}
                  </div>
                ))}
              </div>
            )}
            <div className="border-2 border-dashed rounded-lg p-4 text-center transition-colors border-muted-foreground/20"
              onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
              onDrop={e => { e.preventDefault(); e.stopPropagation(); const dropped = Array.from(e.dataTransfer.files); if (dropped.length) setPendingFiles(prev => [...prev, ...dropped.map(f => ({ file: f, visibleToOperators: pendingFilesVisible }))]); }}
              data-testid="tc-create-file-drop-zone">
              <Upload className="h-5 w-5 mx-auto text-muted-foreground mb-1" />
              <p className="text-xs text-muted-foreground">Drag & drop or click to attach files</p>
              <div className="flex items-center justify-center gap-3 mt-2">
                <input ref={fileInputRef} type="file" multiple className="hidden" onChange={e => { const selected = Array.from(e.target.files); if (selected.length) setPendingFiles(prev => [...prev, ...selected.map(f => ({ file: f, visibleToOperators: pendingFilesVisible }))]); e.target.value = ''; }} data-testid="tc-create-file-input" />
                <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} className="text-xs h-7" data-testid="tc-create-choose-files-btn"><Upload className="h-3 w-3 mr-1" /> Choose Files</Button>
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
                  <input type="checkbox" checked={pendingFilesVisible} onChange={e => setPendingFilesVisible(e.target.checked)} className="rounded border-muted-foreground/30" />
                  Visible to operators
                </label>
              </div>
            </div>
            {pendingFiles.length > 0 && (<div className="space-y-1 mt-3">
              {pendingFiles.map((pf, idx) => (
                <div key={`pf-${pf.file.name}-${idx}`} className="flex items-center gap-2 px-2 py-1.5 border rounded text-xs bg-muted/30">
                  <FileIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" /><span className="flex-1 truncate">{pf.file.name}</span>
                  <span className="text-muted-foreground shrink-0">{pf.file.size < 1024 * 1024 ? `${(pf.file.size / 1024).toFixed(0)} KB` : `${(pf.file.size / (1024 * 1024)).toFixed(1)} MB`}</span>
                  <button type="button" onClick={() => setPendingFiles(prev => prev.map((f, i) => i === idx ? { ...f, visibleToOperators: !f.visibleToOperators } : f))} className={`p-0.5 rounded ${pf.visibleToOperators ? 'text-green-500' : 'text-muted-foreground/40'}`}>{pf.visibleToOperators ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}</button>
                  <button type="button" onClick={() => setPendingFiles(prev => prev.filter((_, i) => i !== idx))} className="text-muted-foreground hover:text-destructive p-0.5"><X className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>)}
          </CardContent>
        </Card>

        {/* Bottom submit */}
        <div className="flex justify-end gap-3 pb-8">
          <Button variant="outline" onClick={goBack} data-testid="tc-create-bottom-cancel-btn">Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-[#ffd108] text-black hover:bg-[#e5bc07] min-w-[140px]" data-testid="tc-create-bottom-submit-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {isEdit ? 'Save Changes' : 'Create Tower Crane Job'}
          </Button>
        </div>
      </div>
      )} {/* end activeTab === 'details' */}

      {/* ── Add Customer Contact Dialog ── */}
      <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}><DialogContent><DialogHeader><DialogTitle>Add New Contact</DialogTitle></DialogHeader>
        <form onSubmit={handleCreateContact} className="space-y-3">
          <div><Label className="text-xs text-muted-foreground">Name *</Label><Input value={contactForm.name} onChange={e => setContactForm(f => ({ ...f, name: e.target.value }))} placeholder="Contact name" required data-testid="tc-new-contact-name" /></div>
          <div><Label className="text-xs text-muted-foreground">Position</Label><Input value={contactForm.position} onChange={e => setContactForm(f => ({ ...f, position: e.target.value }))} placeholder="e.g. Project Manager" /></div>
          <div><Label className="text-xs text-muted-foreground">Phone</Label><Input value={contactForm.phone} onChange={e => setContactForm(f => ({ ...f, phone: e.target.value }))} placeholder="Phone number" /></div>
          <div><Label className="text-xs text-muted-foreground">Email</Label><Input type="email" value={contactForm.email} onChange={e => setContactForm(f => ({ ...f, email: e.target.value }))} placeholder="Email address" /></div>
          <DialogFooter><Button type="button" variant="outline" onClick={() => setContactDialogOpen(false)}>Cancel</Button><Button type="submit" disabled={savingContact} className="bg-[#ffd108] text-black hover:bg-[#e5bc07]">{savingContact ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Create Contact</Button></DialogFooter>
        </form>
      </DialogContent></Dialog>

      {/* ── Add Site Contact Dialog ── */}
      <Dialog open={siteContactDialogOpen} onOpenChange={setSiteContactDialogOpen}><DialogContent><DialogHeader><DialogTitle>Add Site Contact</DialogTitle></DialogHeader>
        <form onSubmit={handleCreateSiteContact} className="space-y-3">
          <div><Label className="text-xs text-muted-foreground">Name *</Label><Input value={newSiteContact.name} onChange={e => setNewSiteContact(f => ({ ...f, name: e.target.value }))} placeholder="Contact name" required data-testid="tc-new-site-contact-name" /></div>
          <div><Label className="text-xs text-muted-foreground">Job Title</Label><Input value={newSiteContact.job_title} onChange={e => setNewSiteContact(f => ({ ...f, job_title: e.target.value }))} placeholder="e.g. Site Supervisor" /></div>
          <div><Label className="text-xs text-muted-foreground">Phone</Label><Input value={newSiteContact.phone} onChange={e => setNewSiteContact(f => ({ ...f, phone: e.target.value }))} placeholder="Phone number" /></div>
          <div><Label className="text-xs text-muted-foreground">Email</Label><Input type="email" value={newSiteContact.email} onChange={e => setNewSiteContact(f => ({ ...f, email: e.target.value }))} placeholder="Email address" /></div>
          <DialogFooter><Button type="button" variant="outline" onClick={() => setSiteContactDialogOpen(false)}>Cancel</Button><Button type="submit" disabled={savingContact} className="bg-[#ffd108] text-black hover:bg-[#e5bc07]">{savingContact ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Add Contact</Button></DialogFooter>
        </form>
      </DialogContent></Dialog>
    </div>
  );
}
